import * as React from 'react';
import TimeTrackingService from '../services/TimeTrackingService';
import ChangeRequestService from '../services/ChangeRequestService';
import { DateRange } from './DateRangeFilter';
import { TimeTracking, ensureZ, short } from './ttImportUtils';
import css from './TTImportWizard.module.css';

interface TTImportExecuteStepProps {
  importStep: number;
  previewRows: any[];
  fallbackEnabled: boolean;
  importRunning: boolean;
  importCompleted: boolean;
  importProgress: { done: number; total: number };
  importLog: string[];
  importResult: { created: number; cr: number; errors: number };
  importFile: File | null;
  activeEmployeeId?: number | null;
  dateRange: DateRange | null;
  selectedWAs: number[];
  uiLedger: Record<number, { originalStart: string; originalEnd: string }>;
  setImportStep: (v: number) => void;
  setFallbackEnabled: (v: boolean) => void;
  setImportRunning: (v: boolean) => void;
  setImportProgress: React.Dispatch<React.SetStateAction<{ done: number; total: number }>>;
  setImportLog: React.Dispatch<React.SetStateAction<string[]>>;
  setImportResult: React.Dispatch<React.SetStateAction<{ created: number; cr: number; errors: number }>>;
  setImportCompleted: (v: boolean) => void;
  setTimeTrackings: React.Dispatch<React.SetStateAction<TimeTracking[]>>;
  setApprovedCrMap: React.Dispatch<React.SetStateAction<Record<number, { start: string; end: string }>>>;
  setPendingCrMap: React.Dispatch<React.SetStateAction<Record<number, { start: string; end: string }>>>;
  setRestoredOriginalMap: React.Dispatch<React.SetStateAction<Record<number, { start: string; end: string }>>>;
  markCrPresence: (ttId: number, has: boolean) => void;
  persistLedger: (next: Record<number, { originalStart: string; originalEnd: string }>) => void;
  setToast: React.Dispatch<React.SetStateAction<{ type: 'success' | 'error' | 'info'; text: string } | null>>;
  loadTimeTrackings: (range: DateRange, workAreaIds: number[], employeeId?: number | null) => Promise<void>;
  onClose: () => void;
  setFileInputKey: React.Dispatch<React.SetStateAction<number>>;
  setLocalFileInputKey: React.Dispatch<React.SetStateAction<number>>;
}

export function TTImportExecuteStep({
  importStep,
  previewRows,
  fallbackEnabled,
  importRunning,
  importCompleted,
  importProgress,
  importLog,
  importResult,
  importFile,
  activeEmployeeId,
  dateRange,
  selectedWAs,
  uiLedger,
  setImportStep,
  setFallbackEnabled,
  setImportRunning,
  setImportProgress,
  setImportLog,
  setImportResult,
  setImportCompleted,
  setTimeTrackings,
  setApprovedCrMap,
  setPendingCrMap,
  setRestoredOriginalMap,
  markCrPresence,
  persistLedger,
  setToast,
  loadTimeTrackings,
  onClose,
  setFileInputKey,
  setLocalFileInputKey,
}: TTImportExecuteStepProps): React.JSX.Element | null {

  const clearCrMaps = (ttId: number, startZ: string, endZ: string) => {
    setApprovedCrMap((prev) => ({ ...prev, [ttId]: { start: startZ, end: endZ } }));
    setPendingCrMap((prev) => { const n = { ...prev } as any; delete n[ttId]; return n; });
    setRestoredOriginalMap((prev) => { const n = { ...prev } as any; delete n[ttId]; return n; });
    markCrPresence(ttId, false);
  };

  const runImport = async () => {
    if (importRunning) return;
    setImportRunning(true);
    setImportLog([`Starting import with ${previewRows.length} preview rows`]);
    setImportResult({ created: 0, cr: 0, errors: 0 });
    const todo = previewRows.filter(
      (r) => (r.status === 'ok' || r.status === 'warn') && r.startIsoUtc && r.endIsoUtc
    );
    if (todo.length === 0) {
      const skipped = previewRows.filter((r) => r.status === 'skip').length;
      const errors = previewRows.filter((r) => r.status === 'error').length;
      setImportLog((prev) => [...prev, `No importable rows (ok/warn=0). Skipped=${skipped}, Errors=${errors}.`]);
      setImportRunning(false);
      return;
    }
    setImportProgress({ done: 0, total: todo.length });
    const batchId = importFile
      ? `batch::${importFile.name}::${importFile.size}::${importFile.lastModified}`
      : `batch-${Date.now()}`;
    const chunkSize = 20;
    const parallel = 1;
    const chunks: any[][] = [];
    for (let i = 0; i < todo.length; i += chunkSize) chunks.push(todo.slice(i, i + chunkSize));
    const sleep = (ms: number) => new Promise((res) => setTimeout(res, ms));
    const runWithRetry = async (fn: () => Promise<void>, rowIndex: number) => {
      let attempt = 0;
      while (true) {
        try { await fn(); return; } catch (e: any) {
          const isNet = (typeof e?.isNetworkError === 'function' && e.isNetworkError()) || e?.message === 'Network Error';
          if (isNet && attempt < 2) { attempt++; await sleep(400 * attempt); continue; }
          throw e;
        }
      }
    };
    const RESOLVE_STRATEGY: 'truncate' | 'reject' = 'truncate';

    const directUpdateFallback = async (ttId: number, startZ: string, endZ: string) => {
      let originalStart = '';
      let originalEnd = '';
      try {
        const cur = await TimeTrackingService.fetchTimeTracking(ttId);
        originalStart = cur?.start_time || '';
        originalEnd = cur?.end_time || '';
      } catch {}
      await TimeTrackingService.updateTimeTracking(ttId, { start_time: startZ, end_time: endZ } as any);
      if (originalStart && originalEnd) {
        persistLedger({ ...uiLedger, [ttId]: { originalStart, originalEnd } });
      }
      clearCrMaps(ttId, startZ, endZ);
    };

    const upsertAndApproveCR = async (r: any, startZ: string, endZ: string) => {
      const ttId = Number(r.ttId);
      if (!Number.isFinite(ttId)) throw new Error('ttId missing for CR row');
      const approveOpts = { resolve_conflicts: RESOLVE_STRATEGY, override_start_time: startZ, override_end_time: endZ };

      const tryApprove = async (crId: number) => {
        try {
          await ChangeRequestService.approve(crId, approveOpts);
          clearCrMaps(ttId, startZ, endZ);
          setImportResult((prev) => ({ ...prev, cr: prev.cr + 1 }));
          setImportLog((prev) => [...prev, `\u2713 Row ${r.rowIndex} CR approved`]);
        } catch (e: any) {
          const detail = e?.response?.data?.detail || '';
          if (typeof detail === 'string' && /Pending|PENDING|Nur\s+PENDING/i.test(detail)) {
            await ChangeRequestService.submitEmployeeReview(crId, {
              new_start_time: startZ, new_end_time: endZ, employee_notes: 'Batch import (auto-approve)',
            });
            await ChangeRequestService.approve(crId, approveOpts);
            clearCrMaps(ttId, startZ, endZ);
            setImportResult((prev) => ({ ...prev, cr: prev.cr + 1 }));
            setImportLog((prev) => [...prev, `\u2713 Row ${r.rowIndex} CR approved (after review)`]);
          } else { throw e; }
        }
      };

      const patch: any = { new_start_time: startZ, new_end_time: endZ };
      if (r.workAreaId != null) patch.new_work_area = Number(r.workAreaId);
      if (r.breakMinutes != null && !Number.isNaN(Number(r.breakMinutes))) patch.break_minutes = Number(r.breakMinutes);

      let pending: any = null;
      try { pending = await ChangeRequestService.fetchByTimeTrackingId(ttId, 'PENDING'); } catch {}
      if (pending?.id) {
        await ChangeRequestService.submitEmployeeReview(pending.id, { ...patch, employee_notes: 'Batch import (update + auto-approve)' });
        await tryApprove(pending.id);
        return;
      }

      let approved: any = null;
      try { approved = await ChangeRequestService.fetchByTimeTrackingId(ttId, 'APPROVED'); } catch {}
      if (approved?.id) {
        try { await ChangeRequestService.reopen(approved.id); } catch {}
        let p2: any = null;
        try { p2 = await ChangeRequestService.fetchByTimeTrackingId(ttId, 'PENDING'); } catch {}
        const targetId = p2?.id || approved.id;
        await ChangeRequestService.submitEmployeeReview(targetId, { ...patch, employee_notes: 'Batch import (reopen + auto-approve)' });
        await tryApprove(targetId);
        return;
      }

      const created = await ChangeRequestService.create({
        time_tracking: ttId, time_tracking_id: ttId, ...patch, reason: 'IMPORT_CORRECTION',
      } as any);
      let newId = created?.id;
      if (!newId) { try { const pCr = await ChangeRequestService.fetchByTimeTrackingId(ttId, 'PENDING'); if (pCr?.id) newId = pCr.id; } catch {} }
      if (!newId) { try { const anyCr = await ChangeRequestService.fetchByTimeTrackingId(ttId); if (anyCr?.id) newId = anyCr.id; } catch {} }
      if (!newId) throw new Error('CR create returned no id (API sent no body and fallback did not find a CR)');
      try { await ChangeRequestService.submitEmployeeReview(newId, { ...patch, employee_notes: 'Batch import (create + auto-approve)' }); } catch {}
      await tryApprove(newId);
    };

    const runChunk = async (chunk: any[]) => {
      for (const r of chunk) {
        try {
          await runWithRetry(async () => {
            const startZ = ensureZ(r.startIsoUtc);
            const endZ = ensureZ(r.endIsoUtc);
            if (r.type === 'NEW') {
              const payload: any = {
                employee: Number(r.employeeId), work_area: Number(r.workAreaId),
                start_time: startZ, end_time: endZ, break_start: null, break_end: null,
              };
              const created = await TimeTrackingService.createTimeTracking({ ...payload, created_via: 'import_file', import_batch_id: batchId });
              setImportResult((prev) => ({ ...prev, created: prev.created + 1 }));
              try {
                if (created && created.id) {
                  (created as any).created_via = (created as any).created_via || 'import_file';
                  let within = true;
                  if (typeof activeEmployeeId === 'number') within = within && Number(created.employee) === Number(activeEmployeeId);
                  if (Array.isArray(selectedWAs) && selectedWAs.length > 0) within = within && selectedWAs.includes(Number((created as any).work_area));
                  if (dateRange && (created as any).start_time) {
                    const dt = new Date((created as any).start_time);
                    const from = new Date((dateRange as any).from);
                    const to = new Date((dateRange as any).to);
                    if (!Number.isNaN(from.getTime()) && !Number.isNaN(to.getTime())) within = within && dt >= from && dt <= to;
                  }
                  if (within) {
                    setTimeTrackings((prev) => [created as any, ...prev]);
                    try {
                      const fresh = await TimeTrackingService.fetchTimeTracking((created as any).id);
                      setTimeTrackings((prev) => prev.map((i) => i.id === fresh.id ? ({ ...i, ...fresh } as any) : i));
                    } catch {}
                  }
                }
              } catch {}
            } else {
              await upsertAndApproveCR(r, startZ!, endZ!);
            }
          }, r.rowIndex);
          setImportLog((prev) => [...prev, `\u2713 Row ${r.rowIndex} ${r.type === 'NEW' ? 'created' : 'CR created/updated'}`]);
        } catch (e: any) {
          const status = e?.response?.status || e?.status;
          const url = e?.config?.url;
          const method = e?.config?.method;
          const payloadReq = e?.config?.data;
          const msg = e && e.message ? e.message : String(e);
          const stack = e && e.stack ? String(e.stack).split('\n').slice(0, 2).join(' | ') : '';
          if (status === 401) {
            setImportLog((prev) => [...prev, `\u2717 Row ${r.rowIndex} failed: Session abgelaufen`, `  Bitte loggen Sie sich erneut ein und versuchen Sie den Import nochmal.`]);
            setToast({ type: 'error', text: 'Session abgelaufen \u2013 bitte neu einloggen' });
            setImportResult((prev) => ({ ...prev, errors: prev.errors + 1 }));
            setImportRunning(false);
            setImportCompleted(true);
            return;
          } else if (status === 400) {
            const detail = e?.response?.data?.detail || e?.response?.data?.message || e?.data?.detail || '';
            setImportLog((prev) => [...prev, `\u2717 Row ${r.rowIndex} failed: Ung\u00fcltige Daten (400)`, detail ? `  Detail: ${short(detail)}` : `  payload: ${short(payloadReq)}`].filter(Boolean));
          } else {
            setImportLog((prev) => [...prev, `\u2717 Row ${r.rowIndex} failed: ${status ?? '?'} ${method ?? ''} ${url ?? ''}`, `  message: ${msg}`, stack ? `  stack: ${stack}` : undefined, `  payload: ${short(payloadReq)}`, `  body: ${short(e?.response?.data)}`].filter(Boolean) as string[]);
          }
          if (fallbackEnabled && r.type === 'CR') {
            try {
              await directUpdateFallback(Number(r.ttId), ensureZ(r.startIsoUtc)!, ensureZ(r.endIsoUtc)!);
              setImportResult((prev) => ({ ...prev, cr: prev.cr + 1 }));
              setImportLog((prev) => [...prev, `\u2713 Row ${r.rowIndex} applied via direct update fallback`]);
            } catch {}
          }
          setImportResult((prev) => ({ ...prev, errors: prev.errors + 1 }));
        } finally {
          setImportProgress((prev) => ({ ...prev, done: prev.done + 1 }));
        }
      }
    };
    try {
      for (let i = 0; i < chunks.length; i += parallel) {
        const group = chunks.slice(i, i + parallel);
        await Promise.all(group.map(runChunk));
      }
      setImportLog((prev) => [...prev, 'Done.']);
      try { if (dateRange) await loadTimeTrackings(dateRange, selectedWAs, activeEmployeeId); } catch {}
      setImportCompleted(true);
    } finally {
      setImportRunning(false);
    }
  };

  if (importStep === 4) {
    const importableCount = previewRows.filter((r) => r.status === 'ok' || r.status === 'warn').length;
    return (
      <div className={css.rulesBlock}>
        <div className={css.rulesTitle}>Rules</div>
        <div className={css.rulesHint}>Configure import behavior (overlaps, absences, corrections).</div>
        <label className={css.checkboxLabel}>
          <input type="checkbox" checked={fallbackEnabled} onChange={(e) => setFallbackEnabled(e.target.checked)} className={css.checkbox} />
          <span className={css.checkboxText}>Enable failsafe direct update if CR workflow fails</span>
        </label>
        <div className={css.rulesActions}>
          <button className={css.btnBack} onClick={() => setImportStep(3)}>Back</button>
          <button className={css.btnPrimary} disabled={!importableCount} onClick={() => setImportStep(5)} data-testid="tt-import-rules-next-button">
            Next{importableCount ? ` (${importableCount})` : ''}
          </button>
        </div>
      </div>
    );
  }

  if (importStep === 5) {
    return (
      <div className={css.importBlock}>
        <div className={css.importTitle}>Import</div>
        <div className={css.importHint}>Ready to start import. Progress and summary will be shown here.</div>
        <div className={css.importSummary}>
          <strong className={css.importSummaryStrong}>Summary:</strong> Created {importResult.created} &middot; CR {importResult.cr} &middot; Errors {importResult.errors}
        </div>
        <div style={{ marginBottom: 12 }}>
          <div className={css.progressText}>Progress: {importProgress.done}/{importProgress.total}</div>
          <div className={css.logBox}>
            {importLog.map((l, i) => (<div key={i} className={css.logLine}>{l}</div>))}
          </div>
        </div>
        <div className={css.importActions}>
          <button className={importRunning || importCompleted ? css.btnBackDisabled : css.btnBack} onClick={() => setImportStep(4)} disabled={importRunning || importCompleted}>Back</button>
          {!importCompleted ? (
            <button className={css.btnPrimary} disabled={importRunning} onClick={runImport} data-testid="tt-import-start-button">Start import</button>
          ) : (
            <button className={css.btnSuccess} onClick={() => { onClose(); setImportStep(1); setLocalFileInputKey((k: number) => k + 1); setFileInputKey((k: number) => k + 1); }} data-testid="tt-import-close-button">Close</button>
          )}
        </div>
      </div>
    );
  }

  return null;
}
